import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme from './theme/theme';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import AppVersionWatcher from './components/AppVersionWatcher';
import ProtectedRoute from './components/ProtectedRoute';
import RoleGate from './components/RoleGate';
import LoadingScreen from './components/LoadingScreen';
import AppLayout from './layouts/AppLayout';
import PublicLayout from './layouts/PublicLayout';
import LoginPage from './features/auth/LoginPage';
import GuestAccessPage from './features/auth/GuestAccessPage';
import ChangePasswordPage from './features/auth/ChangePasswordPage';
import DashboardPage from './features/dashboard/DashboardPage';
import LogbuchPage from './features/logbook/LogbuchPage';
import EintragVornehmenPage from './features/entry/EintragVornehmenPage';
import BenachrichtigungenPage from './features/notifications/BenachrichtigungenPage';
import BenutzerPage from './features/admin/BenutzerPage';
import TaetigkeitenPage from './features/admin/TaetigkeitenPage';
import AuditLogPage from './features/admin/AuditLogPage';
import StatistikPage from './features/statistics/StatistikPage';
import EignungsverfahrenPage from './features/assessment/EignungsverfahrenPage';
import LehrgangSrhtPage from './features/srhtCourse/LehrgangSrhtPage';
import PruefungSrhtPage from './features/srhtExam/PruefungSrhtPage';
import SrhtChecklistPage from './features/srhtChecklist/SrhtChecklistPage';
import PublicPdfReturnPage from './features/pdf/PublicPdfReturnPage';
import EinsatzPage from './features/einsatz/EinsatzPage';
import DataExchangePage from './features/einsatz/DataExchangePage';
import UebungsplanungPage from './features/uebung/UebungsplanungPage';
import RueckmeldungenPage from './features/rueckmeldung/RueckmeldungenPage';
import SelbstreflexionPage from './features/selfReflection/SelbstreflexionPage';
import {
  canAccessEinsatzFeatures,
  canAccessAdvancedFeatures,
  canAccessAssessmentModule,
  canAccessSrhtExamModule,
  canAccessSrhtCourseModule,
  canCreateLogbuchEntries,
  canAccessStatistics,
  canAccessLogbuchFeatures,
  canManageActivityTypes,
  canAccessRueckmeldungenFeatures,
  canAccessUebungsplanungFeatures,
  canViewDataExchange,
  canUseSelfReflection,
  getAuthenticatedHomePath,
  hasOnlyDataExchangeReaderRole,
} from './config/roleAccess';

function AppRoutes() {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) return <LoadingScreen />;

  if (isAuthenticated && hasOnlyDataExchangeReaderRole(user)) {
    return (
      <Routes>
        <Route
          path="/einsatz/datenaustausch"
          element={
            <ProtectedRoute>
              <AppLayout>
                <DataExchangePage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/einsatz/datenaustausch" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/pdf-return/:token"
        element={
          <PublicLayout>
            <PublicPdfReturnPage />
          </PublicLayout>
        }
      />
      <Route
        path="/gastzugang"
        element={
          <PublicLayout>
            <GuestAccessPage />
          </PublicLayout>
        }
      />
      <Route
        path="/login"
        element={
          isAuthenticated ? (
            <Navigate to={getAuthenticatedHomePath(user)} />
          ) : (
            <PublicLayout>
              <LoginPage />
            </PublicLayout>
          )
        }
      />
      <Route path="/register" element={<Navigate to="/login" replace />} />

      {/* Protected routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <AppLayout>
              <DashboardPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/logbuch"
        element={
          <ProtectedRoute>
            <AppLayout>
              <RoleGate check={canAccessLogbuchFeatures}>
                <LogbuchPage />
              </RoleGate>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/eintrag-vornehmen"
        element={
          <ProtectedRoute>
            <AppLayout>
              <RoleGate check={canCreateLogbuchEntries}>
                <EintragVornehmenPage />
              </RoleGate>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/benachrichtigungen"
        element={
          <ProtectedRoute>
            <AppLayout>
              <BenachrichtigungenPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/einsatz"
        element={
          <ProtectedRoute>
            <AppLayout>
              <RoleGate check={canAccessEinsatzFeatures}>
                <EinsatzPage />
              </RoleGate>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/einsatz/datenaustausch"
        element={
          <ProtectedRoute>
            <AppLayout>
              <RoleGate check={canViewDataExchange} fallback={getAuthenticatedHomePath(user)}>
                <DataExchangePage />
              </RoleGate>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/uebung"
        element={
          <ProtectedRoute>
            <AppLayout>
              <RoleGate check={canAccessUebungsplanungFeatures}>
                <UebungsplanungPage />
              </RoleGate>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/rueckmeldung"
        element={
          <ProtectedRoute>
            <AppLayout>
              <RoleGate check={canAccessRueckmeldungenFeatures}>
                <RueckmeldungenPage />
              </RoleGate>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/rueckmeldung/selbstreflexion"
        element={
          <ProtectedRoute>
            <AppLayout>
              <RoleGate check={canUseSelfReflection}>
                <SelbstreflexionPage />
              </RoleGate>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/benutzer"
        element={
          <ProtectedRoute>
            <AppLayout>
              <RoleGate check={canAccessAdvancedFeatures}>
                <BenutzerPage />
              </RoleGate>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/taetigkeiten"
        element={
          <ProtectedRoute>
            <AppLayout>
              <RoleGate check={canManageActivityTypes}>
                <TaetigkeitenPage />
              </RoleGate>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/passwort-aendern"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ChangePasswordPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/statistik"
        element={
          <ProtectedRoute>
            <AppLayout>
              <RoleGate check={canAccessStatistics}>
                <StatistikPage />
              </RoleGate>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/eignungsverfahren"
        element={
          <ProtectedRoute>
            <AppLayout>
              <RoleGate check={canAccessAssessmentModule}>
                <EignungsverfahrenPage />
              </RoleGate>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/lehrgang-srht"
        element={
          <ProtectedRoute>
            <AppLayout>
              <RoleGate check={canAccessSrhtCourseModule}>
                <LehrgangSrhtPage />
              </RoleGate>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/pruefung-srht"
        element={
          <ProtectedRoute>
            <AppLayout>
              <RoleGate check={canAccessSrhtExamModule}>
                <PruefungSrhtPage />
              </RoleGate>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/laufzettel-srht"
        element={
          <ProtectedRoute>
            <AppLayout>
              <RoleGate check={canAccessLogbuchFeatures}>
                <SrhtChecklistPage />
              </RoleGate>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/audit"
        element={
          <ProtectedRoute>
            <AppLayout>
              <RoleGate check={canAccessAdvancedFeatures}>
                <AuditLogPage />
              </RoleGate>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<Navigate to={isAuthenticated ? getAuthenticatedHomePath(user) : '/login'} />} />
      <Route path="*" element={<Navigate to={isAuthenticated ? getAuthenticatedHomePath(user) : '/login'} replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AuthProvider>
          <NotificationProvider>
            <AppVersionWatcher />
            <AppRoutes />
          </NotificationProvider>
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
